import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RefreshCw, ShieldAlert } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/services/BaseService";
import { KillSwitchState } from "@/types/module-kill-switch";
import {
  killSwitchKeys,
  useKillSwitchStates,
  useToggleKillSwitch,
} from "./hooks/useKillSwitches";
import { ModuleStateCard } from "./components/ModuleStateCard";
import { ToggleModuleDialog } from "./components/ToggleModuleDialog";
import { AuditLogSection } from "./components/AuditLogSection";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";

export const ModulesPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("settings");
  const {
    data: states,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useKillSwitchStates();
  const toggleMutation = useToggleKillSwitch();

  const [target, setTarget] = useState<KillSwitchState | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const closeDialog = () => {
    setTarget(null);
    setServerError(null);
  };

  const handleSubmit = async (reason: string | null) => {
    if (!target) return;
    const nextEnabled = !target.enabled;
    const moduleName = t(`modules.codes.${target.module}.label`);

    setServerError(null);
    try {
      await toggleMutation.mutateAsync({
        module: target.module,
        payload: { enabled: nextEnabled, reason },
      });
      toast.success(
        nextEnabled
          ? t("modules.toasts.enabled", { name: moduleName })
          : t("modules.toasts.disabled", { name: moduleName }),
      );
      closeDialog();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 400) {
          toast.warning(error.message, {
            description: t("modules.toasts.noopTitle"),
          });
          queryClient.invalidateQueries({ queryKey: killSwitchKeys.all });
          closeDialog();
          return;
        }
        if (error.statusCode === 403) {
          toast.error(t("modules.toasts.forbidden"));
          closeDialog();
          return;
        }
        setServerError(error.message);
        return;
      }
      setServerError(t("modules.toasts.genericError"));
    }
  };

  if (isLoading) {
    return (
      <div className="p-12 text-sm text-muted-foreground">
        {t("modules.page.loading")}
      </div>
    );
  }

  if (isError || !states) {
    return (
      <div className="flex flex-col gap-3 p-12 max-w-md">
        <div className="flex items-center gap-2 text-destructive">
          <ShieldAlert className="size-5" />
          <span className="text-sm font-medium">
            {t("modules.page.loadError")}
          </span>
        </div>
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={() => refetch()}
          className="w-fit"
        >
          {t("modules.page.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 p-12 max-w-6xl">
      <header className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-2 max-w-3xl">
            <h1 className="text-2xl font-semibold text-foreground">
              {t("modules.page.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("modules.page.description")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden md:inline">
              {t("modules.page.polling")}
            </span>
            <Button
              type="button"
              variant="new_outline"
              size="sm"
              disabled={isFetching}
              onClick={() => refetch()}
            >
              <RefreshCw
                className={isFetching ? "size-3.5 animate-spin" : "size-3.5"}
              />
              {t("modules.page.refresh")}
            </Button>
          </div>
        </div>
      </header>

      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-foreground">
            {t("modules.states.sectionTitle")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("modules.states.sectionDescription")}
          </p>
        </div>
        <div className="flex flex-col gap-4">
          {states.map((state) => (
            <ModuleStateCard
              key={state.module}
              state={state}
              disabled={!canWrite || toggleMutation.isPending}
              onToggle={(s) => {
                setServerError(null);
                setTarget(s);
              }}
            />
          ))}
        </div>
      </section>

      <AuditLogSection />

      <ToggleModuleDialog
        state={target}
        isPending={toggleMutation.isPending}
        serverError={serverError}
        onClose={closeDialog}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
